import { useState, useEffect, useCallback, useRef } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import {
  searchMarketplace,
  MarketplaceSearchFilters,
  MarketplaceSearchResponse,
  FacetBucket,
} from '../api/marketplaceApi';
import { MarketplaceDocument } from 'types/marketplace';

export interface UseMarketplaceSearchResult {
  results: MarketplaceDocument[];
  total: number;
  facets: {
    use_case_tags: FacetBucket[];
    doc_type: FacetBucket[];
    item_type: FacetBucket[];
  };
  loading: boolean;
  error: string | null;
  search: (query: string) => void;
  setFilters: (filters: MarketplaceSearchFilters) => void;
  setSort: (sort: 'relevance' | 'rating' | 'subscribers' | 'recent') => void;
  loadMore: () => void;
  hasMore: boolean;
}

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 300;

const EMPTY_FACETS = {
  use_case_tags: [] as FacetBucket[],
  doc_type: [] as FacetBucket[],
  item_type: [] as FacetBucket[],
};

export function useMarketplaceSearch(): UseMarketplaceSearchResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const [results, setResults] = useState<MarketplaceDocument[]>([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState<MarketplaceSearchResponse['facets']>(EMPTY_FACETS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<MarketplaceSearchFilters>({});
  const [sort, setSort] = useState<'relevance' | 'rating' | 'subscribers' | 'recent'>('relevance');
  const [offset, setOffset] = useState(0);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const search = useCallback((term: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setQuery(term);
      setOffset(0);
      setResults([]);
    }, DEBOUNCE_MS);
  }, []);

  const updateFilters = useCallback((newFilters: MarketplaceSearchFilters) => {
    setFilters(newFilters);
    setOffset(0);
    setResults([]);
  }, []);

  const updateSort = useCallback((newSort: 'relevance' | 'rating' | 'subscribers' | 'recent') => {
    setSort(newSort);
    setOffset(0);
    setResults([]);
  }, []);

  const loadMore = useCallback(() => {
    setOffset((prev) => prev + PAGE_SIZE);
  }, []);

  useEffect(() => {
    if (!apiClients?.searchClient || !organizationId || !query.trim()) {
      return;
    }

    let cancelled = false;

    const doSearch = async () => {
      setLoading(true);
      setError(null);

      try {
        const response: MarketplaceSearchResponse = await searchMarketplace(
          apiClients.searchClient,
          {
            query: query.trim(),
            filters: Object.keys(filters).length > 0 ? filters : undefined,
            sort,
            limit: PAGE_SIZE,
            offset,
          }
        );

        if (!cancelled) {
          if (offset === 0) {
            setResults(response.results);
          } else {
            setResults((prev) => [...prev, ...response.results]);
          }
          setTotal(response.total);
          setFacets(response.facets);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Search failed';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    doSearch();

    return () => {
      cancelled = true;
    };
  }, [apiClients, organizationId, query, filters, sort, offset]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const hasMore = results.length < total;

  return {
    results,
    total,
    facets,
    loading,
    error,
    search,
    setFilters: updateFilters,
    setSort: updateSort,
    loadMore,
    hasMore,
  };
}
